import { useEffect, useRef } from "react";
import RevealWrapper from "../../base/RevealWrapper";
import { BUSINESS } from "../../../mocks/siteData";

export interface HeroContent {
  eyebrow: string;
  headline: string;
  subheadline: string;
  primaryCTA?: { label: string; href: string; external?: boolean };
  secondaryCTA?: { label: string; href: string; external?: boolean };
  bgImage: string;
  overlayStrength?: string;
}

interface Props {
  content: HeroContent;
}

export default function HeroSection({ content }: Props) {
  const { eyebrow, headline, subheadline, primaryCTA, secondaryCTA, bgImage } = content;
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (imgRef.current) {
        const scrolled = window.scrollY;
        imgRef.current.style.transform = `translateY(${scrolled * 0.18}px) scale(1.05)`;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const renderCTA = (cta: { label: string; href: string; external?: boolean }, className: string) =>
    cta.external ? (
      <a href={cta.href} target="_blank" rel="noopener noreferrer" className={className}>
        {cta.label}
        <i className="ri-arrow-right-line" />
      </a>
    ) : (
      <a href={cta.href} className={className}>
        {cta.label}
        <i className="ri-arrow-right-line" />
      </a>
    );

  return (
    <section
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      aria-label={`Hero — ${headline}`}
    >
      {/* Background image with subtle parallax */}
      <div className="absolute inset-0 overflow-hidden">
        <img
          ref={imgRef}
          src={bgImage}
          alt={headline}
          title={`The Lash Bar — ${headline}`}
          className="w-full h-full object-cover object-center will-change-transform"
          style={{ transform: "scale(1.05)" }}
        />
      </div>

      {/* Cinematic overlay layers */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0.15)_0%,rgba(0,0,0,0.72)_100%)]" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/80" />
      {/* Vignette corners */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(0,0,0,0.5)_0%,transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(0,0,0,0.5)_0%,transparent_50%)]" />

      {/* Vertical decorative text */}
      <div className="absolute right-6 top-1/2 -translate-y-1/2 hidden xl:flex flex-col items-center gap-3 z-10">
        <div className="w-px h-16 bg-white/15" />
        <span className="font-sans text-[9px] tracking-[0.35em] uppercase text-white/25 [writing-mode:vertical-rl]">
          Lehi · Utah · 4.9 Stars
        </span>
        <div className="w-px h-16 bg-white/15" />
      </div>

      {/* Centered main content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 pt-28 pb-24 flex flex-col items-center text-center">
        <RevealWrapper direction="fade">
          {/* Eyebrow with flanking lines */}
          <div className="flex items-center justify-center gap-5 mb-8">
            <span className="block w-10 h-px bg-gold/60" />
            <span className="section-eyebrow text-gold/90 tracking-[0.3em]">{eyebrow}</span>
            <span className="block w-10 h-px bg-gold/60" />
          </div>
        </RevealWrapper>

        {/* Headline — huge serif, split into visual lines */}
        <RevealWrapper delay={0.1}>
          <h1 className="font-serif font-light text-white leading-[1.05] tracking-tight mb-8 max-w-5xl"
            style={{ fontSize: "clamp(2.4rem, 6vw, 5.5rem)" }}
          >
            {headline}
          </h1>
        </RevealWrapper>

        <RevealWrapper delay={0.2}>
          <p className="font-sans text-base md:text-lg text-white/65 leading-relaxed mb-12 max-w-2xl font-light">
            {subheadline}
          </p>
        </RevealWrapper>

        {/* CTAs */}
        <RevealWrapper delay={0.3}>
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
            {primaryCTA
              ? renderCTA(primaryCTA, "btn-gold text-sm px-12 py-5")
              : (
                <a
                  href={BUSINESS.bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold text-sm px-12 py-5"
                >
                  Book Your Luxury Appointment
                  <i className="ri-arrow-right-line" />
                </a>
              )}
            {secondaryCTA && renderCTA(secondaryCTA, "btn-outline text-sm px-12 py-5")}
          </div>
        </RevealWrapper>

        {/* Trust signals strip */}
        <RevealWrapper delay={0.45}>
          <div className="mt-16 flex flex-wrap items-center justify-center gap-8 md:gap-12">
            <div className="flex items-center gap-3">
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((i) => (
                  <i key={i} className="ri-star-fill text-gold text-base" />
                ))}
              </div>
              <span className="font-sans text-sm font-medium text-white/80">4.9 · 901 Google Reviews</span>
            </div>
            <span className="w-px h-5 bg-white/25 hidden md:block" />
            <div className="flex items-center gap-2.5">
              <i className="ri-award-line text-gold text-base" />
              <span className="font-sans text-sm font-medium text-white/80">Voted Utah&apos;s Best Lashes</span>
            </div>
            <span className="w-px h-5 bg-white/25 hidden md:block" />
            <div className="flex items-center gap-2.5">
              <i className="ri-map-pin-line text-gold text-base" />
              <span className="font-sans text-sm font-medium text-white/80">Lehi, Utah 84043</span>
            </div>
            <span className="w-px h-5 bg-white/25 hidden md:block" />
            <a
              href={BUSINESS.phoneTel}
              className="flex items-center gap-2.5 font-sans text-sm font-medium text-white/80 hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-phone-line text-gold text-base" />
              {BUSINESS.phone}
            </a>
          </div>
        </RevealWrapper>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10">
        <span className="font-sans text-[9px] tracking-[0.25em] uppercase text-white/30">Scroll</span>
        <div className="w-px h-10 bg-white/20 hero-scroll-line" />
      </div>
    </section>
  );
}
